// SPDX-License-Identifier: AGPL-3.0-or-later

import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {openRoomOutfit} from '@app/features/voice_scene/commands/RoomOutfitCommands';
import {
	canSendEmote,
	ownsRoomEmote,
	type SlapRefusal,
	sendEmote,
	sendPoint,
	sendSlap,
	slapRefusal,
} from '@app/features/voice_scene/commands/RoomSignalCommands';
import {hasOpenModal} from '@app/features/voice_scene/scene/RoomInput';
import type {RoomBodyEmote} from '@app/features/voice_scene/scene/VoiceSceneTypes';
import RoomShortcutGuide from '@app/features/voice_scene/state/RoomShortcutGuide';
import {i18n, type MessageDescriptor} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {useEffect} from 'react';

const SHORTCUTS: ReadonlyArray<'wave' | RoomBodyEmote> = ['wave', 'dance', 'jump', 'crouch', 'punch', 'faint'];

export const STAND_UP_DESCRIPTOR = msg({
	message: 'Stand up first (E)',
	comment: 'Voice call 3D room: tooltip on a body movement while your character sits; E is the key that stands it up.',
});

// Why T did not slap, in a short toast. The toast shows one at a time, so presses in a row replace it.
const SLAP_HINTS: Record<SlapRefusal, MessageDescriptor> = {
	seated: STAND_UP_DESCRIPTOR,
	crowded: msg({
		message: 'The room is too crowded',
		context: 'voice-scene-slap-hint',
		comment:
			'Voice call 3D room: short hint after pressing T (slap). Pointing and slapping are off while the room holds more people than characters drawn.',
	}),
	limited: msg({
		message: 'Wait a moment',
		context: 'voice-scene-slap-hint',
		comment: 'Voice call 3D room: short hint after pressing T (slap) too soon after the last movement.',
	}),
	no_target: msg({
		message: 'Nobody in front of you',
		context: 'voice-scene-slap-hint',
		comment: 'Voice call 3D room: short hint after pressing T (slap) with no character standing in front of yours.',
	}),
};
const SLAP_HINT_MS = 1500;

// The picker needs the cursor: the walking camera's pointer lock lets go first, as the outfit does.
function openMovements(): void {
	if (document.pointerLockElement) document.exitPointerLock();
	RoomShortcutGuide.toggleMore();
}

// ? the shortcut card (Shift or not, on any layout), G every movement, O the outfit.
function guideKey(event: KeyboardEvent): (() => void) | null {
	if (event.key === '?') return RoomShortcutGuide.toggleCard;
	if (event.shiftKey) return null;
	if (event.code === 'KeyG') return openMovements;
	if (event.code === 'KeyO') return openRoomOutfit;
	return null;
}

export function useRoomEmotes({
	canvas,
	standing,
	enabled,
}: {
	canvas: HTMLCanvasElement | null;
	standing: boolean;
	enabled: boolean;
}): void {
	useEffect(() => {
		if (!enabled || !canvas) return;
		const doc = canvas.ownerDocument;
		const win = doc.defaultView ?? window;
		const previousTabIndex = canvas.getAttribute('tabindex');
		canvas.tabIndex = 0;
		const focusCanvas = (): void => {
			canvas.tabIndex = 0;
			canvas.focus({preventScroll: true});
		};
		canvas.addEventListener('pointerdown', focusCanvas);
		const onKeyDown = (event: KeyboardEvent): void => {
			if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey || event.repeat || event.isComposing)
				return;
			if (
				doc.activeElement !== canvas ||
				canvas.closest('dialog, [role="dialog"], [role="alertdialog"], [contenteditable]')
			)
				return;
			if (hasOpenModal(doc)) return;
			// The guide's keys, seated too; Esc first closes what they opened.
			const guide = guideKey(event);
			if (guide || (event.key === 'Escape' && RoomShortcutGuide.closeAll())) {
				event.preventDefault();
				event.stopPropagation();
				guide?.();
				return;
			}
			// By their place on the keyboard, running (Shift) too: B points, T slaps; only standing, like the body
			// movements. A T that does not slap says why.
			if (event.code === 'KeyB') {
				if (!standing || !canSendEmote()) return;
				event.preventDefault();
				event.stopPropagation();
				sendPoint();
				return;
			}
			if (event.code === 'KeyT') {
				const tried = standing && canSendEmote();
				const refusal = tried && sendSlap() ? null : slapRefusal(standing);
				if (!tried && !refusal) return;
				event.preventDefault();
				event.stopPropagation();
				if (refusal)
					ToastCommands.createToast({type: 'info', children: i18n._(SLAP_HINTS[refusal]), timeout: SLAP_HINT_MS});
				return;
			}
			if (event.shiftKey) return;
			const kind = /^[1-6]$/.test(event.key) ? SHORTCUTS[Number(event.key) - 1] : undefined;
			if (!kind || (kind !== 'wave' && !standing) || !canSendEmote() || !ownsRoomEmote(kind)) return;
			event.preventDefault();
			event.stopPropagation();
			sendEmote({kind});
		};
		// Capture before the composer's type-anywhere listener can claim the key and focus.
		win.addEventListener('keydown', onKeyDown, true);
		return () => {
			win.removeEventListener('keydown', onKeyDown, true);
			canvas.removeEventListener('pointerdown', focusCanvas);
			if (previousTabIndex === null) canvas.removeAttribute('tabindex');
			else canvas.setAttribute('tabindex', previousTabIndex);
		};
	}, [canvas, standing, enabled]);
}
